import { useEffect, useState } from "react";
import { Pulse } from "@phosphor-icons/react";
import { UploadKeys } from "../lib/api";

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
};

const Stat = ({ id, label, value, sub }) => (
  <div className="px-4 py-3 border-b-2 sm:border-b-0 sm:border-r-2 border-black last:border-0 min-w-0" data-testid={`watcher-status-${id}`}>
    <p className="label-mono text-[10px] text-neutral-500">{label}</p>
    <p className="font-mono text-lg font-bold truncate">{value}</p>
    {sub && <p className="font-mono text-[11px] text-neutral-500 truncate" title={sub} data-testid={`watcher-status-${id}-file`}>{sub}</p>}
  </div>
);

export function WatcherStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    const load = () => UploadKeys.status().then(setSt).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (!st) return null;
  return (
    <section className="border-2 border-black mt-6" data-testid="watcher-status-section">
      <div className="px-4 py-3 border-b-2 border-black flex items-center gap-2 font-display font-bold tracking-tighter">
        <Pulse weight="bold" size={18} /> 00 · STATUS
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 border-b-2 border-black">
        <Stat id="files" label="FILES SYNCED" value={st.files_synced} />
        <Stat id="last-sync" label="LAST FILE SYNCED" value={ago(st.last_synced_at)} sub={st.last_synced_file} />
        <Stat id="pcs" label="PCS ONLINE" value={`${st.devices.filter((d) => d.online).length} / ${st.devices.length}`} />
      </div>
      {st.devices.length === 0 ? (
        <p className="p-4 text-sm text-neutral-500" data-testid="watcher-status-empty">No PC has checked in yet. Start the watcher (step 3 or 4 below) and it appears here within a minute.</p>
      ) : (
        <ul className="divide-y-2 divide-black" data-testid="watcher-device-list">
          {st.devices.map((d) => (
            <li key={d.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`watcher-device-${d.id}`}>
              <div className="min-w-0">
                <p className="font-mono text-sm font-bold truncate">{d.hostname} <span className="text-neutral-500 font-normal">· {d.key_label || "key"} · {d.mode === "startup" ? "starts with PC" : "manual run"}</span></p>
                <p className="font-mono text-[11px] text-neutral-500 truncate">
                  LAST SEEN {ago(d.last_seen_at).toUpperCase()} · {d.synced_this_run} SYNCED{d.failed_this_run ? ` · ${d.failed_this_run} RETRIES` : ""} THIS RUN · {d.watch_path}
                </p>
                {d.last_error && <p className="font-mono text-[11px] text-amber-400 truncate" data-testid={`watcher-device-error-${d.id}`}>LAST ERROR: {d.last_error}</p>}
              </div>
              <span className={`chip ${d.online ? "chip-live" : ""}`} data-testid={`watcher-device-state-${d.id}`}>{d.key_revoked ? "KEY REVOKED" : d.online ? "ONLINE" : "OFFLINE"}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
